<template>
  <div
    id="add-comment"
    :class="containerClass"
  >
    <div class="add-comment-container">
      <div class="border">
        <!-- <div class="icon" /> -->
        <div class="inner">
          <div class="editor-container">
            <div class="text-container">
              <div
                v-if="editingCommentIdentifier"
                class="editing-comment-message"
              >
                {{ $t('comments.edit_comment') }}
              </div>
              <div
                v-if="replyingToIdentifier"
                class="replying-to-message"
              >
                {{ $t('comments.replying_to') }}:&nbsp;
                <span>{{ replyingToData.content | stripHTML | summarize(50) }}</span>
              </div>
              <div class="textarea-container">
                <div class="text">
                  <quill-editor
                    ref="quillEditor"
                    name="comment"
                    :options="quillOptions"
                    @keydown.native.enter.exact="onEnterKey"
                    @change="onQuillEditorChange"
                  />
                </div>
                <add-comment-media
                  v-if="mode === 'inline'"
                  :cloudinary-widget-loading="cloudinaryWidgetLoading"
                  :comment-media-file="commentMediaFile"
                  :is-saving-comment="isSavingComment"
                  :open-media-widget="openMediaWidget"
                  :remove-media="removeMedia"
                />
              </div>
            </div>
            <add-comment-media
              v-if="mode === 'floating'"
              :cloudinary-widget-loading="cloudinaryWidgetLoading"
              :comment-media-file="commentMediaFile"
              :is-saving-comment="isSavingComment"
              :open-media-widget="openMediaWidget"
              :remove-media="removeMedia"
            />
          </div>
        </div>
        <div :class="{ 'button-container': true, 'editing': Boolean(editingCommentIdentifier) }">
          <template v-if="editingCommentIdentifier">
            <b-tooltip
              :label="$t('global.save')"
              position="is-top"
              type="is-dark"
            >
              <b-button
                class="plain-button"
                :disabled="!dataIsValid"
                :loading="isSavingComment"
                @click="saveEditingComment"
              >
                <check-icon class="accept" />
              </b-button>
            </b-tooltip>
            <b-tooltip
              :label="$t('global.cancel')"
              position="is-top"
              type="is-dark"
            >
              <b-button
                class="plain-button"
                :disabled="isSavingComment"
                @click="resetCommentEditor"
              >
                <x-icon class="cancel" />
              </b-button>
            </b-tooltip>
          </template>
          <b-button
            v-else
            :class="buttonClass"
            :disabled="!dataIsValid"
            :loading="isSavingComment"
            :title="$t('comments.comment')"
            @click="submitComment"
          >
            <span>{{ $t('comments.comment') }}</span>
          </b-button>
          <b-tooltip
            v-if="replyingToIdentifier"
            :label="$t('global.cancel')"
            position="is-top"
            type="is-dark"
          >
            <b-button
              class="plain-button replying-cancel"
              :disabled="isSavingComment"
              @click="resetCommentEditor"
            >
              <x-icon class="cancel" />
            </b-button>
          </b-tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { quillEditor } from 'vue-quill-editor'

import CheckIcon from '@/components/icons/CheckIcon.vue'
import CloudinaryTexts from '@/cloudinaryTexts.json'
import { sanitizeCloudinaryFilename } from '@/format'
import AddCommentMedia from './AddCommentMedia.vue'
import XIcon from '../icons/XIcon.vue'
import { scrollToElementByIdWithOffset } from '../../utils/scrollToElement'
import vue from '../../main'

export default {
  name: 'AddCommentBar',
  components: {
    AddCommentMedia,
    CheckIcon,
    quillEditor,
    XIcon
  },
  props: {
    commentedContentIdentifier: {
      type: String,
      required: true
    },
    commentedContentAppLabel: {
      type: String,
      required: true
    },
    commentedContentModel: {
      type: String,
      required: true
    },
    mediaFolder: {
      type: String,
      required: true
    },
    mode: {
      type: String,
      default: 'floating'
    }
  },
  data() {
    return {
      cloudinaryWidgetLoading: false
    }
  },
  computed: {
    ...mapGetters('comments', ['replyingToData']),
    ...mapGetters('Users', ['userFullName']),
    ...mapState('comments', [
      'updatedCommentIdentifier', 'commentHtml', 'commentMediaFile', 'editingCommentIdentifier',
      'isSavingComment', 'replyingToIdentifier'
    ]),
    quillOptions() {
      return {
        theme: 'snow',
        modules: {
          toolbar: null,
          clipboard: {
            matchVisual: false
          }
        },
        placeholder: vue.$t('comments.enter_comment')
      }
    },
    quill() {
      return this.$refs.quillEditor.quill
    },
    buttonClass() {
      return this.mode === 'floating' ? 'comment-button' : 'verbose-comment-button'
    },
    containerClass() {
      const classes = ['container', 'is-widescreen', `mode-${this.mode}${this.mode === 'inline' ? ' white-box' : ''}`]

      if (this.editingCommentIdentifier) {
        classes.push('editing-comment')
      }

      return classes
    },
    dataIsValid() {
      if (this.commentHtml || this.commentMediaFile) {
        return true
      }

      return false
    }
  },
  watch: {
    updatedCommentIdentifier() {
      if (this.updatedCommentIdentifier) {
        setTimeout(() => {
          scrollToElementByIdWithOffset(`comment-${this.updatedCommentIdentifier}`)
        }, 300)
      }
    },
    editingCommentIdentifier() {
      this.quill.setContents(this.quill.clipboard.convert(this.commentHtml))

      if (this.editingCommentIdentifier && this.mode === 'inline') {
        scrollToElementByIdWithOffset('add-comment')
      }
    },
    replyingToIdentifier() {
      if (this.editingCommentIdentifier) this.quill.setContents(this.quill.clipboard.convert(''))

      if (this.replyingToIdentifier && this.mode === 'inline') {
        scrollToElementByIdWithOffset('add-comment')
      }
    }
  },
  destroyed() {
    // Reset state
    this.resetCommentEditor()
  },
  methods: {
    onEnterKey(event) {
      // For some reason the .prevent is not working with the quill editor, for that reason,
      // we are forced to remove the last character if the user presses enter.
      if (event.code === 'Enter') {
        this.removeLastCharacterFromQuillEditor()
      }

      if (this.editingCommentIdentifier) {
        this.saveEditingComment()
      } else {
        this.submitComment()
      }
    },
    async submitComment() {
      console.log('dada')
      if (!this.isSavingComment && (!this.isQuillEmpty(this.quill) || this.commentMediaFile)) {
        await this.$store.dispatch(
          'comments/addComment',
          {
            content: JSON.stringify({ delta: this.quill.getContents(), html: this.commentHtml }),
            commentedContentAppLabel: this.commentedContentAppLabel,
            commentedContentModel: this.commentedContentModel,
            commentedContentIdentifier: this.commentedContentIdentifier
          }
        )

        this.quill.setText('')
      }
    },
    removeLastCharacterFromQuillEditor() {
      const cursorIndex = this.quill.getSelection().index
      const originalTextValue = this.quill.getText()
      const textValue = originalTextValue.slice(0, cursorIndex - 1)
        + originalTextValue.slice(cursorIndex, originalTextValue.length)

      this.quill.setText(textValue)
      this.quill.setSelection(cursorIndex - 1, 0)
    },
    isQuillEmpty(quill) {
      if ((quill.getContents()['ops'] || []).length !== 1) { return false }

      return quill.getText().trim().length === 0
    },
    openMediaWidget() {
      this.cloudinaryWidgetLoading = true

      window.cloudinary.createUploadWidget({
        upload_preset: 'mvp_preset_participante',
        folder: this.mediaFolder,
        cropping: true,
        multiple: false,
        language: this.$i18n.locale,
        text: CloudinaryTexts,
        show_powered_by: false,
        sources: [
          'local', 'camera'
        ],
        clientAllowedFormats: ['image', 'video'],
        prepareUploadParams: (cb, params) => {
          const publicId = sanitizeCloudinaryFilename(
            `${this.userFullName} - ${Math.floor(Date.now() / 1000)}`
          )

          cb({
            ...params,
            public_id: publicId
          })
        }
      }, (error, result) => {
        if (!error && result) {
          if (result.event === 'display-changed' && result.data.info === 'shown') {
            this.cloudinaryWidgetLoading = false
          } else if (result.event === 'success') {
            this.setMediaFile(result.info)
          }
        }
      }).open()
    },
    onQuillEditorChange(value) {
      this.$store.commit('comments/setCommentHtml', value.html)
    },
    saveEditingComment() {
      this.$store.dispatch('comments/updateComment', {
        content: JSON.stringify({ delta: this.quill.getContents(), html: this.commentHtml }),
        commentedContentAppLabel: this.commentedContentAppLabel,
        commentedContentModel: this.commentedContentModel,
        commentedContentIdentifier: this.commentedContentIdentifier
      })
    },
    setMediaFile(mediaInfo) {
      const value = {
        publicId: mediaInfo.public_id,
        width: mediaInfo.width,
        height: mediaInfo.height,
        type: mediaInfo.type,
        resourceType: mediaInfo.resource_type,
        version: mediaInfo.version,
        format: mediaInfo.format
      }

      this.$store.commit('comments/setCommentMedia', value)
    },
    removeMedia() {
      this.$store.commit('comments/removeCommentMedia')
    },
    resetCommentEditor() {
      this.$store.commit('comments/resetCommentEditor')
    }
  }
}
</script>

<style lang="scss" scoped>
.mode-floating {
  position: fixed;
  bottom: 30px;
  left: 0;
  right: 0;
  max-width: 718px;

  @include respond(mobile) {
    max-width: calc(100% - 32px);
  }

  .border {
    display: flex;
  }

  .inner {
    flex-grow: 1;
  }

  .add-comment-container {
    background: #FFFFFF;
    border: 4px solid #fff;
    box-shadow: 0px 2px 4px rgba(81, 107, 152, 0.16);
    border-radius: 30px;

    @include respond(mobile) {
      background: #ebebeb;
    }

    .border {
      padding: 13px 20px;
      margin: 0;

      .button-container {
        display: flex;
      }

      .text {
        min-height: 26px;
      }
    }
  }

  &.editing-comment .add-comment-container {
    background-color: $gray-ultralight;
    border-color: $gray-light;
  }

  .comment-button span { display: none; }

  @include respond(mobile) {
    .border .button-container.editing {
      display: flex;
      flex-direction: column-reverse;
      justify-content: center;
      align-items: flex-end;
      margin-left: 10px;

      .accept {
        height: 19px;
      }

      .cancel {
        height: 25px;
        position: relative;
        right: 2px;
      }
    }
  }
}

.mode-inline {
  margin-bottom: 30px;

  .add-comment-container {
    .border {
      @include respond(mobile-up) {
        padding: 28px 37px;
      }

      @include respond(mobile) {
        padding: 22px 26px;
      }

      .button-container {
        display: flex;
        justify-content: flex-end;
        margin-top: 10px;
      }

      .text {
        border: 2px solid #e1e1e1;
        border-radius: 5px;
        padding: 5px;
        max-height: none;
      }
    }
  }

  .button-container.editing {
    margin-bottom: -6px;
  }
}

.button-container.editing {
  .b-tooltip:last-child {
    margin-left: 8px;
  }
}

.editing-comment-message, .replying-to-message {
  text-align: left;
  padding-left: 3px;
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 6px;

  span {
    font-weight: 400;
  }
}

.add-comment-container {
  .border {
    margin: 4px;
    padding: 14px 20px;

    border-radius: 26px;

    /* .icon {
         display: none;

         width: 32px;
         height: 26px;
         margin-right: 12px;

         background: url("../../assets/comments/comment-smile.svg") center no-repeat;
       } */

    .editor-container {
      display: flex;

      .text {
        flex-grow: 1;
      }
    }

    .text-container {
      display: flex;
      flex-direction: column;
      flex-grow: 1;
    }

    .textarea-container {
      display: flex;
      flex-direction: row;
      flex-grow: 1;
    }

    .text {
      width: 100%;
      min-height: 24px;
      max-height: 100px;
    }

    .button-container {
      display: flex;
      align-items: center;

      .comment-button {
        width: 26px;
        height: 24px;
        margin-left: 12px;
        padding: 0px;

        border: none;
        background: url("../../assets/comments/comment-send.svg") center no-repeat;
        transition: $buttonBackgroundTransition;

        &.is-loading {
          background: none;
        }
      }

      .confirm {
        color: $green;
      }

      .cancel {
        color: $red;
      }

      .plain-button {
        padding: 3px;
      }

      .confirm, .cancel {
        cursor: pointer;
      }

      .verbose-comment-button {
        background: $blue;
        color: #fff;
        line-height: 30px;
        height: 30px;
        border-radius: 5px;
        font-size: 14px;
        font-weight: 600;
        border: none;
        transition: $buttonBackgroundTransition;

        &:hover {
          background: darken($blue, 6);
        }
      }
    }
  }

  .replying-cancel {
    margin-left: 6px;
  }
}

.mode-inline {
  ::v-deep .ql-container.ql-snow {
    .ql-editor.ql-blank::before {
      font-size: 14px;
      color: #808080;
    }
  }
}

.accept {
  color: $green;
  height: 21px;
  width: auto;
}

.cancel {
  height: 22px;
  width: auto;
}

button.is-loading {
  svg {
    visibility: hidden;
  }
}

.spinner {
  width: 28px;
  height: 28px;
  display: flex;
  justify-content: center;
  align-items: center;
}

::v-deep .ql-container.ql-snow {
  border: none;
  font-family: Nunito Sans;

  .ql-editor {
    max-height: 100px;
    padding: 3px;

    font-size: 14px;
    color: rgba(56, 56, 56, 0.6);
  }

  .ql-editor.ql-blank::before {
    left: 2px;
    font-style: normal;
  }
}
</style>
